import { useState } from "react";

import { Box, Wrap, WrapItem } from "@chakra-ui/react";
import { isEmpty } from "lodash";

import { Tag } from "@/components/display";
import {
  CreatableSelect,
  Editable,
  EditableField,
  EditablePreview,
  Option,
  useIsDirty,
} from "@/components/form";
import { useDebouncedState } from "@/hooks";
import {
  asOptions,
  useCreateOccupation,
  useOccupations,
} from "@/services/hooks/typeahead";
import { createId } from "@/utils";

import { EditableFieldLabel } from "./editable-field-label";
import { EditableFieldProps } from "./types";

export const OccupationsEditableField = ({
  defaultValue,
  isReadOnly = false,
  onChange,
  onSave,
  onCancel,
}: EditableFieldProps<Option[]>) => {
  const [nextValue, setNextValue] = useState(defaultValue);

  const isDirty = useIsDirty(
    defaultValue?.map((x) => x.value).sort(),
    nextValue?.map((x) => x.value).sort()
  );

  const [searchQuery, debouncedSearchQuery, setSearchQuery] =
    useDebouncedState("");

  const {
    data: occupations = [],
    setInterimData: setInterimOccupations,
    isLoading: isLoadingOccupations,
  } = useOccupations({ search: debouncedSearchQuery }, { select: asOptions });

  const { mutateAsync: createOccupation } = useCreateOccupation({
    onMutate: ({ label }) => {
      return setInterimOccupations((draft) => {
        draft?.elements.push({ id: createId(), label });
      });
    },
  });

  return (
    <Box w="full">
      <EditableFieldLabel isDirty={isDirty}>Occupation</EditableFieldLabel>

      <Editable
        label="Occupation"
        isReadOnly={isReadOnly}
        hasValue={!isEmpty(defaultValue)}
        onSave={async () => {
          await onSave?.(nextValue);
        }}
        onCancel={() => {
          setNextValue(defaultValue);
          onCancel?.();
        }}
      >
        <EditablePreview>
          <Wrap spacing={1}>
            {defaultValue?.map((item) => (
              <WrapItem key={item.value}>
                <Tag>{item.label}</Tag>
              </WrapItem>
            ))}
          </Wrap>
        </EditablePreview>

        <EditableField>
          <CreatableSelect
            defaultValue={defaultValue}
            value={nextValue}
            options={occupations}
            inputValue={searchQuery}
            placeholder="Search for occupations..."
            tagVariant="outline"
            isMulti
            isLoading={isLoadingOccupations}
            onSearchQueryChange={(value) => {
              setSearchQuery(value);
            }}
            onChange={(value) => {
              setNextValue(value);
              onChange?.(value);
            }}
            onCreateOption={async (inputValue) => {
              const { id, label, image } = await createOccupation({
                label: inputValue,
              });

              setNextValue((prevValue) => [
                ...(prevValue || []),
                { value: id.toString(), label, image },
              ]);

              onChange?.(nextValue);
            }}
          />
        </EditableField>
      </Editable>
    </Box>
  );
};
